/**
 * Locale & currency auto-detection
 * --------------------------------
 * On a visitor's first visit, detects their preferred language (browser
 * settings) and their country (geo-IP, with a browser-locale fallback),
 * then switches the storefront via Shopify's native localization endpoint.
 * Currency follows the country automatically through Shopify Markets.
 *
 * - Runs once per browser (persisted in localStorage).
 * - Never overrides a manual choice made in the language/currency selectors.
 * - Does nothing in the theme editor or for bots/crawlers.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'theme_localization_pref';

  // ---------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------
  if (window.Shopify && window.Shopify.designMode) return; // theme editor
  if (navigator.webdriver) return; // bots / automated browsers
  if (!window.fetch || !window.localStorage) return; // very old browsers

  var dataEl = document.querySelector('[data-locale-detect-data]');
  if (!dataEl) return;

  var config;
  try {
    config = JSON.parse(dataEl.textContent);
  } catch (err) {
    return;
  }

  // Debug mode: add ?locale_debug=1 to any URL to log every decision to the
  // console and bypass the once-per-visitor guard so you can retest freely.
  var DEBUG = window.location.search.indexOf('locale_debug=1') !== -1;
  function log() {
    if (!DEBUG || !window.console) return;
    var args = ['[locale-auto-detect]'].concat([].slice.call(arguments));
    window.console.log.apply(window.console, args);
  }
  if (DEBUG) {
    log('debug on. store config:', config);
    log('browser languages:', navigator.languages || [navigator.language]);
  }

  // Remember a manual selection from any localization form (header/footer
  // selectors) so auto-detection never fights the visitor's own choice.
  document.addEventListener('submit', function (evt) {
    var form = evt.target;
    if (form && form.action && form.action.indexOf('/localization') !== -1) {
      try {
        localStorage.setItem(STORAGE_KEY, 'manual');
        log('manual selection recorded — auto-detect will stand down');
      } catch (e) {
        /* storage full/blocked — ignore */
      }
    }
  });

  // Already detected once, or visitor picked manually — stop here.
  // (Debug mode skips this so you can retest without clearing storage.)
  var pref = null;
  try {
    pref = localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return;
  }
  if (pref && !DEBUG) return;
  if (pref) log('stored preference is "' + pref + '" — would normally stop here, continuing because debug is on');

  // ---------------------------------------------------------------------
  // Detection helpers
  // ---------------------------------------------------------------------

  /** Best matching published locale for the browser's languages, or null. */
  function detectLocale() {
    var available = config.locales || [];
    if (available.length < 2) return null;

    var wanted = navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language || ''];

    var lower = available.map(function (code) { return code.toLowerCase(); });

    for (var i = 0; i < wanted.length; i++) {
      var tag = (wanted[i] || '').toLowerCase();
      if (!tag) continue;

      // Exact match first (e.g. "pt-br"), then base language (e.g. "pt").
      var exact = lower.indexOf(tag);
      if (exact !== -1) return available[exact];

      var base = tag.split('-')[0];
      for (var j = 0; j < lower.length; j++) {
        if (lower[j] === base || lower[j].split('-')[0] === base) {
          return available[j];
        }
      }
    }
    return null;
  }

  /** Country code from the browser locale's region subtag, or null. */
  function countryFromBrowser() {
    var tags = navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language || ''];
    for (var i = 0; i < tags.length; i++) {
      var parts = (tags[i] || '').split('-');
      if (parts.length > 1 && /^[A-Za-z]{2}$/.test(parts[parts.length - 1])) {
        return parts[parts.length - 1].toUpperCase();
      }
    }
    return null;
  }

  /** Country code via free geo-IP lookup (GeoJS, CORS-enabled, no key). */
  function countryFromGeoIP() {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller && setTimeout(function () { controller.abort(); }, 4000);

    return fetch('https://get.geojs.io/v1/ip/country.json', {
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (json) {
        if (timer) clearTimeout(timer);
        return json && json.country && /^[A-Za-z]{2}$/.test(json.country)
          ? json.country.toUpperCase()
          : null;
      })
      .catch(function () {
        if (timer) clearTimeout(timer);
        return null;
      });
  }

  // ---------------------------------------------------------------------
  // Apply
  // ---------------------------------------------------------------------

  /** POST to Shopify's localization endpoint and reload on the new setting. */
  function applyLocalization(localeCode, countryCode) {
    try {
      localStorage.setItem(STORAGE_KEY, 'auto');
    } catch (e) {
      return; // can't persist — bail rather than risk a redirect loop
    }

    // Path relative to the locale root, so the new locale prefix is applied
    // cleanly by Shopify (e.g. /fr/products/x -> /products/x -> /es/products/x).
    var root = config.rootUrl || '/';
    var path = window.location.pathname;
    if (root !== '/' && path.indexOf(root) === 0) {
      path = '/' + path.slice(root.length);
    }

    var form = document.createElement('form');
    form.method = 'post';
    form.action = root.replace(/\/$/, '') + '/localization';
    form.style.display = 'none';

    var fields = {
      form_type: 'localization',
      utf8: '✓',
      return_to: path + window.location.search
    };
    if (localeCode) fields.locale_code = localeCode;
    if (countryCode) fields.country_code = countryCode;

    Object.keys(fields).forEach(function (name) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = fields[name];
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }

  function run() {
    var localePromise = config.autoLanguage
      ? Promise.resolve(detectLocale())
      : Promise.resolve(null);

    var countryPromise = config.autoCountry && (config.countries || []).length > 1
      ? countryFromGeoIP().then(function (geo) { return geo || countryFromBrowser(); })
      : Promise.resolve(null);

    Promise.all([localePromise, countryPromise]).then(function (results) {
      var locale = results[0];
      var country = results[1];

      log('detected locale:', locale, '| detected country:', country);

      // Only switch to values the store actually supports.
      if (country && (config.countries || []).indexOf(country) === -1) {
        log('country', country, 'is not in the store markets list — ignoring');
        country = null;
      }

      var localeChanged = locale && locale.toLowerCase() !== (config.currentLocale || '').toLowerCase();
      var countryChanged = country && country !== (config.currentCountry || '');

      log('current locale:', config.currentLocale, '| current country:', config.currentCountry);
      log('will change language:', !!localeChanged, '| will change country/currency:', !!countryChanged);

      if (localeChanged || countryChanged) {
        if (DEBUG) {
          log('DEBUG mode: NOT redirecting. Would switch to',
            { locale_code: localeChanged ? locale : '(unchanged)', country_code: countryChanged ? country : '(unchanged)' });
          return;
        }
        applyLocalization(localeChanged ? locale : null, countryChanged ? country : null);
      } else {
        log('nothing to change — visitor is already on the best match');
        // Everything already matches — mark as done so we don't re-check.
        try {
          localStorage.setItem(STORAGE_KEY, 'auto');
        } catch (e) {
          /* ignore */
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
